import { navigate, RouteComponentProps } from '@gatsbyjs/reach-router';
import React from 'react';
import Loading from '~/components/atom/Loading';
import withErrorBoundary from '~/hocs/withErrorBoundary';
import ErrorScreen from '~/components/page/ErrorScreen';
import AccountLocked from '~/components/page/AccountStatus/AccountLocked';
import AccountCancelled from '~/components/page/AccountStatus/AccountCancelled';
import { ErrorTypes } from '~/Errors';
import { getErrorTypes } from '~/util/errorHandling';
import AccountPaused from '~/components/page/AccountStatus/AccountPaused';
import DHRouter from '~/components/atom/DHRouter';
import SetupAccount from '../Auth/SetupAccount';
import AuthWrapperHOC from '../Auth/components/AuthWrapperHOC';
import { AUTH_PATHS } from '../Auth/constants';
import useGetCurrentAccount from './hooks/useGetCurrentAccount';
import AuthRoutes from './components/AuthRoutes';

type Props = RouteComponentProps;

/**
 * Take care of the authentication and account routing
 */
const AccountSelectionWrapper: React.FCC<Props> = () => {
  const result = useGetCurrentAccount();

  if (!result) return <Loading />;

  const { error, loading, refetch, accountId } = result;

  if (loading) return <Loading />;

  if (error) {
    /**
     * When a user gets deleted while still active inside the app
     * we get a `deletedUserError`. Redirect them to the logout page
     */
    const errorTypes = getErrorTypes(error);
    if (errorTypes.includes(ErrorTypes.deletedUserError)) {
      void navigate(AUTH_PATHS.LOGOUT, {
        replace: true,
        state: { isUserDeleted: true },
      });

      return null;
    } else {
      return <ErrorScreen />;
    }
  }

  if (accountId == null)
    return (
      <AuthWrapperHOC bypassURLCheck>
        <SetupAccount refetchAccounts={refetch} />
      </AuthWrapperHOC>
    );

  /* Keep Logout on top so it is accessible on SetupAccount as well */
  return (
    <DHRouter basepath="/-">
      <AccountLocked path="/locked" />
      <AccountPaused path="/paused" />
      <AccountCancelled path="/cancelled" />
      <AuthRoutes default accountId={accountId} />
    </DHRouter>
  );
};

export default withErrorBoundary(AccountSelectionWrapper, <ErrorScreen />);
